import { Injectable } from '@angular/core';

import { FirebaseError } from 'firebase/app';
import { arrayUnion, doc, DocumentData, DocumentSnapshot, getDoc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { Account } from 'src/app/models/account';
import { Organization } from 'src/app/models/organization';

import { AccountService } from 'src/app/services/account/account.service';
import { DataStoreService } from 'src/app/services/data-store/data-store.service';
import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { UserService } from 'src/app/services/user/user.service';
import { environment } from '../../../environments/environment';

export enum OrganizationErrorCodes {
  OrganizationNotFound = 'organization-not-found',
  OrganizationUnauthorized = 'organization-unauthorized',
  UserNotAuthenticated = 'user',
  FailToCreateOrganization = 'fail-to-create-organization',
}

export class OrganizationError extends Error {
  constructor(readonly code: OrganizationErrorCodes) {
    super(code);
    this.name = 'OrganizationError';
  }
}

@Injectable({
  providedIn: 'root'
})
export class OrganizationService {

  private loadingOrganizationId?: string;

  constructor(
    private accountService: AccountService,
    private dataStore: DataStoreService,
    private firebase: FirebaseService,
    private user: UserService,
  ) {
     this.dataStore.getState$().subscribe(({ newState }) => {
      if (!newState.authUser) {
        // User is logged out, clear the organization if needed
        return;
      }

      // If we don't have a current account but we have an organziation, we should clear the organization
      if (!newState.currentAccount) {
        if (newState.organization) {
          this.dataStore.updateState({ organization: null });
        }

        return;
      }

      // If the current account has an organization, we should make sure we have the organization in state
      if (newState.currentAccount.organizationId) {
        if (!newState.organization || newState.organization.id !== newState.currentAccount.organizationId) {
          if (this.loadingOrganizationId === newState.currentAccount.organizationId) {
            return;
          }
          this.loadingOrganizationId = newState.currentAccount.organizationId;
          this.getOrganization(newState.currentAccount.organizationId, true);
        }
      } else {
        //  Otherwise we should clear the organization from state
        if (newState.organization) {
          this.dataStore.updateState({ organization: null });
        }
      }
    });
  }

  async getOrganization(organizationId: string, updateCurrentOrganization = false): Promise<Organization> {
    const currentOrganization = this.dataStore.getProperty('organization');
    if (currentOrganization && currentOrganization.id === organizationId) {
      this.loadingOrganizationId = undefined;
      return currentOrganization;
    }

    let organizationDoc: DocumentSnapshot<DocumentData, DocumentData>
    try {
      const currentUser = await this.user.getCurrentUserDocument();
      if (currentUser.awsFulfillment) {
        await setDoc(doc(this.firebase.firestore, 'organizations', organizationId), {
          awsFulfillment: currentUser.awsFulfillment
        }, { merge: true });
        await setDoc(doc(this.firebase.firestore, 'users', currentUser.userId), {
          awsFulfillment: null
        }, { merge: true });
      }

      organizationDoc = await getDoc(doc(this.firebase.firestore, 'organizations', organizationId));
    } catch (error) {
      this.loadingOrganizationId = undefined;
      if (error instanceof FirebaseError) {
        if (error.code === 'permission-denied') {
          this.clearOrganizationAccountFromUser(organizationId);
        }
      }
      console.error('Error fetching organization:', error);
      throw new OrganizationError(OrganizationErrorCodes.OrganizationUnauthorized);
    }

    if (!organizationDoc.exists()) {
      this.loadingOrganizationId = undefined;
      throw new OrganizationError(OrganizationErrorCodes.OrganizationNotFound);
    }

    const { ownerId, name, createdAt, awsFulfillment, stripeCustomerId, plan, creditBalance, tid, isPartner, partnerId } = organizationDoc.data() as Organization;
    let maxUsers: number | undefined = undefined;

    if (awsFulfillment) {
      const response = await fetch(`https://4nla33eeb3ce5eg4mclh74cs7q0oxtod.lambda-url.us-east-1.on.aws/entitlements?ProductCode=${awsFulfillment.productCode}&CustomerIdentifier=${awsFulfillment.customerIdentifier}`);
      const data = await response.json();
      if (data.Entitlements && data.Entitlements.length === 1) {
        maxUsers = data.Entitlements[0].Value.IntegerValue;
      }
    }

    const organization = {
      id: organizationId,
      name,
      ownerId,
      createdAt,
      awsFulfillment,
      maxUsers,
      stripeCustomerId,
      plan,
      creditBalance,
      tid,
      // Partner flags drive white-label branding and catalog overrides — the
      // explicit destructuring above must carry them or both silently no-op.
      isPartner,
      partnerId,
    }

    if (updateCurrentOrganization) {
      this.dataStore.updateState({ organization });
    }

    this.loadingOrganizationId = undefined;

    return organization;
  }

  async createOrganization(organizationName: string): Promise<{ organization: Organization, account: Account }> {
    const ownerId = this.firebase.auth.currentUser?.uid;

    if (!ownerId) {
      throw new OrganizationError(OrganizationErrorCodes.UserNotAuthenticated);
    }

    return this.createOrganizationWithOwner(ownerId, organizationName);
  }

  async createOrganizationWithOwner(userId: string, organizationName: string): Promise<{ organization: Organization, account: Account }> {
    const callerUid = this.firebase.auth.currentUser?.uid;
    if (!callerUid || callerUid !== userId) {
      throw new OrganizationError(OrganizationErrorCodes.UserNotAuthenticated);
    }

    let result: { organizationId: string; creditsGranted: number };
    let account: Account;
    try {
      const callable = httpsCallable<
        { name: string },
        { organizationId: string; creditsGranted: number }
      >(this.firebase.functions, 'createOrganization');
      const resp = await callable({
        name: organizationName,
      });
      result = resp.data;

      account = await this.accountService.ensureStudioAccount(result.organizationId);
    } catch (e) {
      console.error('Creating organization - Callable failed:', e);
      // Creating an organization needs a verified phone number (backend, 2026-10-24):
      // verify it on the auth portal, which brings the user back here.
      if ((e as { details?: { code?: string } } | null)?.details?.code === 'phone-verification-required') {
        window.location.assign(`${environment.authBaseUrl}/verify-phone?return_to=${encodeURIComponent(window.location.href)}`);
      }
      throw new OrganizationError(OrganizationErrorCodes.FailToCreateOrganization);
    }

    const organization = {
      id: result.organizationId,
      name: organizationName,
      ownerId: userId,
      createdAt: serverTimestamp(),
      creditBalance: result.creditsGranted,
    };

    return {
      organization,
      account,
    };
  }

  async renameOrganization(organizationId: string, newName: string) {
    const organizationRef = doc(this.firebase.firestore, 'organizations', organizationId);
    await updateDoc(organizationRef, {
      name: newName
    });

    const organization = this.dataStore.getProperty('organization');
    if (!organization || organization.id !== organizationId) {
      return this.getOrganization(organizationId, true);
    }

    organization.name = newName;
    this.dataStore.updateState({ organization });

    return organization;
  }

  async linkAccountToOrganization(accountId: string, organizationId: string) {
    const accountRef = this.accountService.getAccountRef(accountId);
    await updateDoc(accountRef, {
      organizationId: organizationId
    });
  }

  async isOrganizationOwner(organizationId: string, userId: string): Promise<boolean> {
    const organization = await this.getOrganization(organizationId);
    if (!organization) {
      return false;
    }
    return organization.ownerId === userId;
  }

  async addOrganizationToUser(userId: string, organizationId: string) {
    const userRef = doc(this.firebase.firestore, 'users', userId);
    return updateDoc(userRef, {
      organizationIds: arrayUnion(organizationId)
    });
  }

  async clearOrganizationAccountFromUser(organizationId: string) {
    // We have been removed from the organization — clear the cached account and reload
    const currentAccount = this.dataStore.getProperty('currentAccount');
    if (currentAccount && currentAccount.organizationId === organizationId) {
      this.accountService.clearCurrentAccount();
    }
    window.location.reload();
  }
}
